import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { useSettings } from "@/contexts/SettingsContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Upload, FileText, CheckCircle2, XCircle, Clock, IdCard, Award, Mail } from "lucide-react";
import { StatusPill } from "./DashboardLayout";
import { toast } from "sonner";

const STATUS_ICON = {
  approved: <CheckCircle2 className="w-6 h-6 text-emerald-600" />,
  rejected: <XCircle className="w-6 h-6 text-red-600" />,
  pending: <Clock className="w-6 h-6 text-amber-600" />,
  none: <FileText className="w-6 h-6 text-muted-foreground" />,
};

function StatusLine({ status, t }) {
  const map = {
    approved: t("ver.status.approved"),
    pending: t("ver.status.pending"),
    rejected: t("ver.status.rejected"),
    none: t("ver.status.none"),
  };
  return <div className="font-display text-xl font-semibold mt-1">{map[status] || map.none}</div>;
}

function UploadCard({
  title, icon, status, history, onUpload, uploading, error,
  testIdPrefix, fileLabel, extraControls, t, historyKeyField,
}) {
  const fileRef = useRef(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  return (
    <div className="border border-border bg-white p-4 sm:p-6">
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="shrink-0">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="overline">{title}</div>
          <StatusLine status={status} t={t} />
          <div className="mt-3"><StatusPill status={status} /></div>
        </div>
      </div>

      <p className="text-xs sm:text-sm text-muted-foreground mt-5 leading-relaxed">{fileLabel}</p>
      {extraControls}
      <input
        ref={fileRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png,image/webp"
        onChange={(e) => onUpload(e, fileRef)}
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
      />
      <Button
        className="mt-4 rounded-md h-12 w-full sm:w-auto"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        data-testid={`${testIdPrefix}-upload-button`}
      >
        <Upload className="w-4 h-4 mr-2" />
        {uploading ? t("ver.uploading") : t("ver.choose")}
      </Button>
      {error && <div className="text-sm text-destructive mt-3">{error}</div>}

      {history.length > 0 && (
        <>
          <div className="thin-rule my-5" />
          <button
            type="button"
            onClick={() => setHistoryOpen((o) => !o)}
            className="w-full flex items-center justify-between"
            data-testid={`${testIdPrefix}-history-toggle`}
          >
            <span className="overline">{t("ver.history")} ({history.length})</span>
            <span className="text-xs text-muted-foreground font-mono">{historyOpen ? "−" : "+"}</span>
          </button>
          {historyOpen && (
            <div className="mt-3 divide-y divide-border">
              {history.map((h) => (
                <div key={h[historyKeyField]} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{h.original_filename}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {new Date(h.created_at).toLocaleString()}
                    </div>
                    {h.notes && <div className="text-xs italic text-muted-foreground mt-0.5 line-clamp-2">"{h.notes}"</div>}
                  </div>
                  <StatusPill status={h.status} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function VerificationPage() {
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const { settings } = useSettings();
  const [licenseList, setLicenseList] = useState([]);
  const [idList, setIdList] = useState([]);
  const [licenseUploading, setLicenseUploading] = useState(false);
  const [idUploading, setIdUploading] = useState(false);
  const [licenseError, setLicenseError] = useState(null);
  const [idError, setIdError] = useState(null);
  const [docType, setDocType] = useState("national_id");

  const load = async () => {
    const [lic, ids] = await Promise.all([
      api.get("/licenses/me").then((r) => r.data),
      api.get("/identity/me").then((r) => r.data),
    ]);
    setLicenseList(lic);
    setIdList(ids);
  };
  useEffect(() => { load(); }, []);

  const uploadLicense = async (e, ref) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLicenseError(null);
    setLicenseUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/licenses/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("License uploaded — awaiting review");
      await load();
      await refresh();
    } catch (err) {
      setLicenseError(formatApiError(err));
    } finally {
      setLicenseUploading(false);
      if (ref?.current) ref.current.value = "";
    }
  };

  const uploadId = async (e, ref) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIdError(null);
    setIdUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/identity/upload?document_type=${docType}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Identity document uploaded — awaiting review");
      await load();
      await refresh();
    } catch (err) {
      setIdError(formatApiError(err));
    } finally {
      setIdUploading(false);
      if (ref?.current) ref.current.value = "";
    }
  };

  if (!user || user === false || user.role !== "doctor") {
    return <div className="text-sm text-muted-foreground">This section is for doctors only.</div>;
  }

  const idStatus = user.id_document_status || "none";
  const licenseStatus = user.license_status || "none";
  const fullyApproved = idStatus === "approved" && licenseStatus === "approved";
  const stepsDone = (idStatus === "approved" ? 1 : 0) + (licenseStatus === "approved" ? 1 : 0);
  const supportEmail = settings?.contact_email || "support@peptideseries.com";

  return (
    <div data-testid="verification-page" className="space-y-6 sm:space-y-8">
      {/* Compact mobile-friendly header with progress */}
      <div className={`border ${fullyApproved ? "border-emerald-200 bg-emerald-50" : "border-border bg-white"} p-4 sm:p-6`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="overline">{t("ver.overline")}</div>
            <h2 className="font-display text-lg sm:text-2xl font-semibold mt-1 leading-tight">
              {fullyApproved ? t("ver.title.verified") : t("ver.title.required")}
            </h2>
          </div>
          <div className="shrink-0 text-right">
            <div className="font-mono text-xl sm:text-2xl font-bold tracking-tight">
              {stepsDone}<span className="text-muted-foreground">/2</span>
            </div>
            <div className="overline text-[9px] mt-0.5">approved</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4 h-1.5 bg-secondary rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${fullyApproved ? "bg-emerald-500" : "bg-[hsl(var(--accent))]"}`}
            style={{ width: `${(stepsDone / 2) * 100}%` }}
          />
        </div>

        <p className="text-xs sm:text-sm text-muted-foreground mt-4 leading-relaxed">{t("ver.body")}</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
        <UploadCard
          title={t("ver.id")}
          icon={<IdCard className="w-6 h-6 text-muted-foreground" />}
          status={idStatus}
          history={idList}
          onUpload={uploadId}
          uploading={idUploading}
          error={idError}
          testIdPrefix="identity"
          fileLabel={t("ver.id.note")}
          historyKeyField="id_document_id"
          t={t}
          extraControls={
            <div className="mt-3">
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="h-11" data-testid="identity-doc-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="national_id">National ID card</SelectItem>
                  <SelectItem value="passport">Passport</SelectItem>
                  <SelectItem value="driver_license">Driver license</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          }
        />

        <UploadCard
          title={t("ver.license")}
          icon={STATUS_ICON[licenseStatus] || <Award className="w-6 h-6 text-muted-foreground" />}
          status={licenseStatus}
          history={licenseList}
          onUpload={uploadLicense}
          uploading={licenseUploading}
          error={licenseError}
          testIdPrefix="license"
          fileLabel={t("ver.license.note")}
          historyKeyField="license_document_id"
          t={t}
        />
      </div>

      {/* Help block — important for doctors stuck in pending */}
      {!fullyApproved && (
        <div className="border border-dashed border-border bg-secondary/40 p-4 sm:p-5 flex items-center gap-3 sm:gap-4">
          <Mail className="w-5 h-5 text-muted-foreground shrink-0" />
          <div className="flex-1 text-xs sm:text-sm">
            Need help? Email{" "}
            <a
              href={`mailto:${supportEmail}?subject=Verification%20support`}
              className="underline text-[hsl(var(--accent))] break-all"
              data-testid="contact-admin-email"
            >
              {supportEmail}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
